import type { Icon, IDropdownMenuItemProps } from '@kognitos/lattice';
import type { V1AutomationStage } from '@/generated/hooks/grimoire/models';

/**
 * Props for menu items in the version history dropdown.
 * Extends IDropdownMenuItemProps to support all DropdownMenuItem props.
 */
export interface IVersionHistoryMenuItem
  extends Omit<IDropdownMenuItemProps, 'children'> {
  key: string;
  title: string;
  icon?: React.ComponentProps<typeof Icon>['type'];
}

/**
 * Metadata representing a version history item.
 * Used for the onClick callback when selecting an item.
 *
 * Deliberately carries no revision content (description / AOP text): that is
 * fetched per revision via `GetAutomationRevision` (see `useRevisionContents`)
 * so the list request stays small enough for the API gateway's transcoder.
 */
export interface IVersionHistoryItemData {
  revisionId: string; // Raw revision ID without 'v' prefix (e.g., "1.0") - used for rollback API call
  version: string; // Display version with 'v' prefix (e.g., "v1.0") - used for UI display
  title: string;
  author: string;
  showBadge?: boolean;
  stage?: V1AutomationStage;
  subtitle?: string | { title: string; details: string }; // Restore message or other UI subtitle
}

/**
 * Props for the VersionHistoryItem component.
 */
export interface IVersionHistoryItemProps extends IVersionHistoryItemData {
  onClick?: (item: IVersionHistoryItemData) => void;
  menuItems?: IVersionHistoryMenuItem[];
  isSelected?: boolean;
}

/**
 * Version history item with timestamp for grouping purposes.
 * Extends IVersionHistoryItemData with additional props for list rendering.
 */
export interface IVersionHistoryListItem extends IVersionHistoryItemData {
  onClick?: (item: IVersionHistoryItemData) => void;
  menuItems?: IVersionHistoryMenuItem[];
  isSelected?: boolean;
  timestamp: Date;
}

/**
 * Grouped version history items by date.
 */
export interface IVersionHistoryGroup {
  dateKey: string;
  label: string;
  items: IVersionHistoryListItem[];
}

/**
 * State enum for version history item visual variants.
 */
export enum EVersionHistoryItemState {
  DEFAULT = 'default',
  SELECTED = 'selected',
}

/**
 * Keys for menu items in the version history dropdown.
 */
export enum EVersionHistoryMenuItemKey {
  CREATE_DRAFT = 'create-draft',
  RESTORE = 'restore',
}

/**
 * Menu item labels for version history actions.
 */
export interface IVersionHistoryMenuLabels {
  createDraft: string;
  restore: string;
}
